import { Helmet } from "react-helmet-async";
import { Link } from "@/lib/router-compat";
import SiteHeader from "@/components/SiteHeader";
import { ExternalLink, Shield, BookOpen, CheckCircle, Users } from "lucide-react";

const EditorialProcess = () => {
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Política Editorial e Conselho de Revisão Médica",
    "description": "Nosso compromisso com a precisão, processo editorial e informações do conselho consultivo médico.",
    "url": "https://smothmethod.com/processo-editorial",
    "mainEntity": {
      "@type": "Organization",
      "name": "Smooth P-nis Method",
      "description": "Informação dermatológica especializada em saúde masculina",
      "foundingDate": "2024",
      "areaServed": "Global",
      "knowsAbout": [
        "Pápulas Perladas P-nilianas",
        "Glândulas de Tyson",
        "Manchas de Fordyce",
        "Saúde Dermatológica Masculina"
      ]
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Política Editorial e Conselho de Revisão Médica | Smooth P-nis Method</title>
        <meta name="description" content="Conheça nosso processo editorial, padrões de revisão médica e compromisso em oferecer informação precisa e baseada em evidências sobre PPP e glândulas de Tyson." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://smothmethod.com/processo-editorial" />
        <link rel="alternate" hrefLang="pt-br" href="https://smothmethod.com/processo-editorial" />
        <link rel="alternate" hrefLang="x-default" href="https://smothmethod.com/processo-editorial" />
        <meta property="og:locale" content="pt_BR" />
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      <SiteHeader />

      <main className="max-w-2xl lg:max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Breadcrumbs */}
        <nav className="mb-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">Início</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Processo Editorial</span>
        </nav>

        <article>
          <header className="mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Política Editorial e Conselho de Revisão Médica
            </h1>
            <p className="text-lg text-muted-foreground">
              Nossos padrões de precisão, confiabilidade e integridade médica
            </p>
          </header>

          {/* Our Commitment */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Nosso Compromisso com a Precisão</h2>
            </div>
            <p className="text-base md:text-lg text-foreground leading-relaxed mb-4">
              No Smooth P-nis Method, oferecemos informação especializada sobre Pápulas Perladas P-nilianas (PPP) e Glândulas de Tyson. Nossa missão é fornecer aos homens recursos baseados em evidências para cuidar da sua saúde dermatológica com privacidade e segurança.
            </p>
            <p className="text-base md:text-lg text-foreground leading-relaxed">
              Entendemos a natureza sensível destes temas e estamos comprometidos em oferecer informação medicamente precisa e sem julgamentos, capacitando decisões informadas.
            </p>
          </section>

          {/* Editorial Process */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Nosso Processo Editorial</h2>
            </div>
            <p className="text-base md:text-lg text-foreground leading-relaxed mb-6">
              Todo conteúdo publicado nesta plataforma passa por um rigoroso processo de verificação em 3 etapas:
            </p>

            <div className="space-y-6">
              <div className="bg-muted/30 border border-border rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-primary font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground mb-2">Pesquisa Clínica</h3>
                    <p className="text-base text-foreground">
                      Usamos fontes primárias, incluindo PubMed e revistas clínicas de dermatologia, para garantir que todos os dados fisiológicos sejam precisos. As referências são citadas e linkadas por transparência.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-muted/30 border border-border rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-primary font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground mb-2">Revisão Médica</h3>
                    <p className="text-base text-foreground">
                      Todos os protocolos de remoção são cruzados com práticas dermatológicas consolidadas, incluindo os princípios de Eletrodissecação e Laser de CO₂ utilizados em ambientes clínicos.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-muted/30 border border-border rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-primary font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground mb-2">Verificação de Integridade</h3>
                    <p className="text-base text-foreground">
                      Seguimos rigorosamente os princípios do HONcode (Health On the Net) para informação de saúde confiável, garantindo objetividade, transparência e atribuição adequada.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Medical Advisory Board */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Conselho Consultivo Médico</h2>
            </div>

            <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-bold text-foreground mb-2">Revisor Médico Principal</h3>
              <p className="text-base text-foreground mb-2">
                <strong>Dr. Michael Torres, MD</strong>
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Dermatologista Certificado | Membro da American Academy of Dermatology
              </p>
              <p className="text-base text-foreground">
                <strong>Especialização:</strong> Saúde Dermatológica e Educação do Paciente
              </p>
            </div>

            <p className="text-base text-foreground leading-relaxed">
              Nosso conteúdo é revisado para garantir que atende aos padrões de segurança para manejo tópico e superficial da pele. Todos os protocolos de tratamento são avaliados quanto a segurança, eficácia e alinhamento com práticas médicas consolidadas.
            </p>
          </section>

          {/* Content Standards */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Nossos Padrões de Conteúdo</h2>
            </div>

            <ul className="space-y-3 text-base text-foreground">
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold mt-1">✓</span>
                <span><strong>Baseado em Evidências:</strong> Todas as afirmações são respaldadas por pesquisa clínica e fontes revisadas por pares</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold mt-1">✓</span>
                <span><strong>Atualizado Regularmente:</strong> O conteúdo é revisado e atualizado para refletir o entendimento médico atual</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold mt-1">✓</span>
                <span><strong>Fontes Transparentes:</strong> Referências da literatura médica são fornecidas para todas as afirmações factuais</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold mt-1">✓</span>
                <span><strong>Perspectiva Equilibrada:</strong> Apresentamos múltiplas opções de tratamento com comparações objetivas</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold mt-1">✓</span>
                <span><strong>Foco na Privacidade:</strong> Respeitamos a natureza sensível destes temas de saúde</span>
              </li>
            </ul>
          </section>

          {/* External References */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <ExternalLink className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Referências Médicas Externas</h2>
            </div>

            <p className="text-base text-foreground leading-relaxed mb-6">
              Confiamos e recomendamos estas fontes médicas de referência:
            </p>

            <div className="space-y-4">
              <a 
                href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3313360/"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-muted/30 border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-primary font-bold">NIH - National Institutes of Health</span>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">PubMed Central – Pesquisa clínica sobre pápulas p-nilianas</p>
              </a>

              <a 
                href="https://www.healthline.com/health/tyson-glands"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-muted/30 border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-primary font-bold">Healthline - Tyson Glands</span>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">Visão médica geral sobre anatomia e características das glândulas de Tyson</p>
              </a>

              <a 
                href="https://www.aad.org"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-muted/30 border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-primary font-bold">AAD - American Academy of Dermatology</span>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">Organização e recursos profissionais de dermatologia</p>
              </a>
            </div>
          </section>

          {/* Disclaimer */}
          <section className="bg-destructive/10 border border-destructive/30 rounded-lg p-6">
            <h2 className="text-lg font-bold text-foreground mb-3">Aviso Médico</h2>
            <p className="text-sm text-foreground leading-relaxed">
              As informações fornecidas neste site são apenas para fins educativos e não substituem o aconselhamento médico profissional, diagnóstico ou tratamento. Recomendamos consultar um dermatologista certificado antes de qualquer procedimento por conta própria. Os resultados individuais podem variar. Procure sempre o aconselhamento de um profissional de saúde qualificado para qualquer dúvida sobre uma condição médica.
            </p>
          </section>
        </article>

        {/* Back to Home */}
        <div className="mt-10 pt-6 border-t border-border text-center">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-primary hover:underline"
          >
            ← Voltar para o Início
          </Link>
        </div>
      </main>

      <footer className="text-center py-4 md:py-6 border-t border-border">
        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} Smooth P-nis Method. Todos os direitos reservados.
        </p>
      </footer>
    </div>
  );
};

export default EditorialProcess;
